// From azure pipelines UI.
//   Class names have been changed to work with Primer styles
//   Source: https://github.com/microsoft/azure-devops-ui/blob/22b5ae5969d405f4459caf9b020019e95bbded38/packages/azure-pipelines-ui/src/Utilities/Parser.ts#L1
import type {ILine, IParseNode, IParsedFindResult, IActionInfo} from './parser-types'
import {NodeType} from './parser-types'
import type {TemplateResult} from 'lit-html/lit-html'
import {html} from 'lit-html/lit-html'

// #region ANSII section

const ESC = '\u001b'
const TimestampLength = 29
const TimestampRegex = /^.{27}Z /gm
const BrightClassPostfix = '-br'

// match characters that could be enclosing url to cleanly handle url formatting
export const URLRegex = /([{([]*https?:\/\/[a-z0-9]+(?:-[a-z0-9]+)*\.[^\s<>|'"]{2,})/gi
// URLs in logs can be wrapped in punctuation to assist with parsing
const URLPunctuation = {
  '(': ')',
  '[': ']',
  '{': '}',
}
type URLStartPunctuation = keyof typeof URLPunctuation
type URLEndPunctuation = (typeof URLPunctuation)[URLStartPunctuation]
const matchingURLPunctuation = (ch: string): URLEndPunctuation => URLPunctuation[ch as URLStartPunctuation]
const regexpEscape = (ch: string) => `\\${ch}`

/**
 * Regex for matching ANSII escape codes
 * \u001b - ESC character
 * ?: Non-capturing group
 * (?:\u001b[) : Match ESC[
 * (?:[\?|#])??: Match also ? and # formats that we don't supports but want to eat our special characters to get rid of ESC character
 * (?:[0-9]{1,3})?: Match one or more occurances of the simple format we want with out semicolon
 * (?:(?:;[0-9]{0,3})*)?: Match one or more occurances of the format we want with semicolon
 */
// eslint-disable-next-line no-control-regex
export const _ansiEscapeCodeRegex = /(?:\u001b\[)(?:[?|#])?(?:(?:[0-9]{1,3})?(?:(?:;[0-9]{0,3})*)?[A-Z|a-z])/

/**
 * http://ascii-table.com/ansi-escape-sequences.php
 * https://en.wikipedia.org/wiki/ANSI_escape_code#3/4_bit
 * We support sequences of format:
 *  Esc[CONTENTHEREm
 *  Where CONTENTHERE can be of format: VALUE;VALUE;VALUE or VALUE
 *      Where VALUE is SGR parameter https://www.vt100.net/docs/vt510-rm/SGR
 *          We support: 0 (reset), 1 (bold), 3 (italic), 4 (underline), 22 (not bold), 23 (not italic), 24 (not underline), 38 (set fg), 39 (default fg), 48 (set bg), 49 (default bg),
 *                      fg colors - 30 (black), 31 (red), 32 (green), 33 (yellow), 34 (blue), 35 (magenta), 36 (cyan), 37 (white), 90 (grey)
 *                        with more brighness - 91 (red), 92 (green), 93 (yellow), 94 (blue), 95 (magenta), 96 (cyan), 97 (white)
 *                      bg colors - 40 (black), 41 (red), 42 (green), 43 (yellow), 44 (blue), 45 (magenta), 46 (cyan), 47 (white), 100 (grey)
 *                        with more brighness- 101 (red), 102 (green), 103 (yellow), 104 (blue), 105 (magenta), 106 (cyan), 107 (white)
 *  Where m refers to the "Graphics mode"
 *
 * 8-bit color is supported
 *  https://en.wikipedia.org/wiki/ANSI_escape_code#8-bit
 *  Esc[38;5;<n> To set the foreground color
 *  Esc[48;5;<n> To set the background color
 *  n can be from 0-255
 *  0-7 are standard colors that match the 4_bit color palette
 *  8-15 are high intensity colors that match the 4_bit high intensity color palette
 *  16-231 are 216 colors that cover the entire spectrum
 *  232-255 are grayscale colors that go from black to white in 24 steps
 *
 * 24-bit color is also supported
 *  https://en.wikipedia.org/wiki/ANSI_escape_code#24-bit
 *  Esc[38;2;<r>;<g>;<b> To set the foreground color
 *  Esc[48;2;<r>;<g>;<b> To set the background color
 *  Where r,g and b must be between 0-255
 */
// #endregion ANSII section

// #region Pipelines commands
const Resets = {
  Reset: '0',
  Bold: '22',
  Italic: '23',
  Underline: '24',
  Set_Fg: '38',
  Default_Fg: '39',
  Set_Bg: '48',
  Default_Bg: '49',
} as const

type Resets = (typeof Resets)[keyof typeof Resets]

const specials = {
  '1': 'bold',
  '3': 'italic',
  '4': 'underline',
} as {[key: string]: string}

const bgColors = {
  // 40 (black), 41 (red), 42 (green), 43 (yellow), 44 (blue), 45 (magenta), 46 (cyan), 47 (white), 100 (grey)
  '40': 'b',
  '41': 'r',
  '42': 'g',
  '43': 'y',
  '44': 'bl',
  '45': 'm',
  '46': 'c',
  '47': 'w',
  '100': 'gr',
} as {[key: string]: string}

const fgColors = {
  // 30 (black), 31 (red), 32 (green), 33 (yellow), 34 (blue), 35 (magenta), 36 (cyan), 37 (white), 90 (grey)
  '30': 'b',
  '31': 'r',
  '32': 'g',
  '33': 'y',
  '34': 'bl',
  '35': 'm',
  '36': 'c',
  '37': 'w',
  '90': 'gr',
} as {[key: string]: string}

const base8BitColors = {
  // 0 (black), 1 (red), 2 (green), 3 (yellow), 4 (blue), 5 (magenta), 6 (cyan), 7 (white), 8 (grey)
  '0': 'b',
  '1': 'r',
  '2': 'g',
  '3': 'y',
  '4': 'bl',
  '5': 'm',
  '6': 'c',
  '7': 'w',
} as Record<string, string>

//0-255 in 6 increments, used to generate 216 equally incrementing colors
const colorIncrements216 = {
  0: 0,
  1: 51,
  2: 102,
  3: 153,
  4: 204,
  5: 255,
} as Record<number, number>

const PLAIN = 'plain'
const COMMAND = 'command'
const DEBUG = 'debug'
const ERROR = 'error'
const INFO = 'info'
const SECTION = 'section'
const VERBOSE = 'verbose'
const WARNING = 'warning'
const GROUP = 'group'
const END_GROUP = 'endgroup'
const ICON = 'icon'
const NOTICE = 'notice'
const START_ACTION = 'start-action'
const END_ACTION = 'end-action'

const commandToType: {[command: string]: NodeType} = {
  command: NodeType.Command,
  debug: NodeType.Debug,
  error: NodeType.Error,
  info: NodeType.Info,
  section: NodeType.Section,
  verbose: NodeType.Verbose,
  warning: NodeType.Warning,
  notice: NodeType.Notice,
  group: NodeType.Group,
  endgroup: NodeType.EndGroup,
  icon: NodeType.Icon,
  'start-action': NodeType.StartAction,
  'end-action': NodeType.EndAction,
}

const typeToCommand: {[type: string]: string} = {
  '0': PLAIN,
  '1': COMMAND,
  '2': DEBUG,
  '3': ERROR,
  '4': INFO,
  '5': SECTION,
  '6': VERBOSE,
  '7': WARNING,
  '8': GROUP,
  '9': END_GROUP,
  '10': ICON,
  '11': NOTICE,
  '12': START_ACTION,
  '13': END_ACTION,
}

// Store the max command length we support, for example, we support "start-action" which is of length 12
const maxCommandLength = 12
const baseCommands = [COMMAND, DEBUG, ERROR, INFO, SECTION, VERBOSE, WARNING, GROUP, END_GROUP, ICON, NOTICE]
let supportedCommands = [...baseCommands]

// Live logs emit structural markers WITHOUT the "##" prefix, e.g. "[section]",
// "[command]", "[group]". Only these structural commands are recognized in the
// single-bracket form. The annotation-style markers ("[info]"/"[error]"/
// "[warning]"/"[debug]"/"[notice]") and the action markers are intentionally
// excluded, so plain user output such as "[INFO - 12:00]" or "[start-action ...]"
// still renders verbatim. (github/c2c-actions-support#6249)
const liveLogCommands = new Set([COMMAND, SECTION, VERBOSE, GROUP, END_GROUP, ICON])

/** Enable or disable composite action marker parsing (gated by actions_runner_emit_composite_markers). */
export function setCompositeActionsEnabled(enabled: boolean) {
  supportedCommands = enabled ? [...baseCommands, START_ACTION, END_ACTION] : [...baseCommands]
}

export function getType(node: IParseNode) {
  return typeToCommand[node.type]
}
// #endregion Pipelines commands

export function getText(text: string) {
  return (text || '').toLocaleLowerCase()
}
// #endregion Common functions

interface IStyle {
  fg: string
  bg: string
  isFgRGB: boolean
  isBgRGB: boolean
  bold: boolean
  italic: boolean
  underline: boolean
  [key: string]: boolean | string
}

interface IRGBColor {
  r: number
  g: number
  b: number
}

interface IAnsiEscapeCodeState {
  output: string
  style?: IStyle
}

const CharacterType = {
  Standard: 'Standard',
  Search: 'Search',
  EOL: 'EOL',
} as const

type CharacterType = (typeof CharacterType)[keyof typeof CharacterType]

// Set max to prevent any perf degradations
export const maxLineMatchesToParse = 100

const maxMatches = 50
const unsetValue = -1
const newLineChar = '\n'
const hashChar = '#'
const commandStart = '['
const commandEnd = ']'

/**
 * Cross-line parser state carried between incremental `parseLines` calls so the parser can
 * resume from the last fully-committed line boundary instead of re-scanning the whole log.
 * The `lines`/`completedActionLineIndices` arrays are appended to in place; callers pass the
 * persistent arrays (truncated to the committed prefix) so retroactive mutations of already
 * committed nodes (e.g. an `end-action`/`endgroup` arriving later) land on the live objects.
 */
export interface IParseResume {
  /**
   * Absolute offset (into the logical full log) at which to (re)start scanning — must be a line
   * boundary. When `baseOffset` is set, `content` holds only the delta region and this offset is
   * still expressed against the full log (the parser subtracts `baseOffset` to read `content`).
   */
  startIndex: number
  lines: ILine[]
  completedActionLineIndices: number[]
  nodeIndex: number
  hiddenLineCount: number
  currentGroupNode?: IParseNode
  actionStack: IParseNode[]
  /** Capture a checkpoint when a line begins at this offset (the start of the pending tail). */
  checkpointOffset: number
  /**
   * Absolute offset of `content[0]` within the logical full log. Lets the caller pass only the
   * delta region (newly committed lines + pending tail) instead of the whole accumulated buffer,
   * while all recorded offsets (node.start/end, lineStartIndex, checkpoint) remain absolute.
   * Defaults to 0 (i.e. `content` is the whole log).
   */
  baseOffset?: number
}

/** Snapshot of parser state at a committed line boundary, used to resume the next parse. */
export interface IParseCheckpoint {
  lineStartIndex: number
  nodeIndex: number
  hiddenLineCount: number
  currentGroupNode?: IParseNode
  actionStack: IParseNode[]
  /** Number of `lines` committed at this checkpoint (excludes the still-pending tail). */
  linesLength: number
  /** Number of `completedActionLineIndices` committed at this checkpoint. */
  caiLength: number
}

export class Parser {
  /**
   * Converts the content to HTML with appropriate styles, escapes content to prevent XSS
   * @param content
   * @param lineNumber
   * @param findResult
   */
  parse(content: string, lineNumber: number, findResult?: IParsedFindResult): TemplateResult {
    let result = html``
    const states = this.getStates(content)
    for (const x of states) {
      const classNames: string[] = []
      const styles: string[] = []
      const currentText = x.output
      if (x.style) {
        const fg = x.style.fg
        const bg = x.style.bg
        const isFgRGB = x.style.isFgRGB
        const isBgRGB = x.style.isBgRGB
        if (fg && !isFgRGB) {
          classNames.push(`ansifg-${fg}`)
        }
        if (bg && !isBgRGB) {
          classNames.push(`ansibg-${bg}`)
          classNames.push(`d-inline-flex`)
        }
        if (fg && isFgRGB) {
          styles.push(`color:rgb(${fg})`)
        }
        if (bg && isBgRGB) {
          styles.push(`background-color:rgb(${bg})`)
          classNames.push(`d-inline-flex`)
        }
        if (x.style.bold) {
          classNames.push('text-bold')
        }
        if (x.style.italic) {
          classNames.push('text-italic')
        }
        if (x.style.underline) {
          classNames.push('text-underline')
        }
      }

      let output
      let matchIndex = -1
      let prevMatchIndex = 0
      const parseResult = Array(currentText.length).fill(CharacterType.Standard)

      if (findResult) {
        const selectedLine = findResult.selectedLine
        // slice to not degrade perf
        const lines = findResult.lines.slice(0, maxLineMatchesToParse).map(l => l.line)

        // always include selected line
        if (lineNumber === selectedLine || lines.indexOf(lineNumber) !== -1) {
          const textToFind = findResult.text
          /* tslint:disable no-conditional-assignment*/
          let matchCount = 1
          const textContent = getText(currentText)
          while ((matchIndex = textContent.indexOf(getText(textToFind), matchIndex + 1)) !== -1) {
            if (matchCount >= maxMatches) {
              break
            }

            prevMatchIndex = matchIndex + textToFind.length
            // actual match
            const markerText = currentText.substring(matchIndex, prevMatchIndex)
            for (let i = 0; i < markerText.length; i++) {
              // mark the index as found, will be styled later
              parseResult[matchIndex + i] = CharacterType.Search
            }
            matchCount++
          }
          /* tslint:enable no-conditional-assignment*/
        }
      }

      // The select class attribute must be added if the current line matches the selectedLine
      const select = findResult && lineNumber === findResult.selectedLine ? 'select' : ''

      const handleHighlight = (index: number, entry: string): TemplateResult | undefined => {
        // There is a URL that needs to be properly styled. There are 2 scenarios that need to be taken care of,
        // a standard URL substring and one that must include extra styling because of search
        // For non-urls, the only extra styling that must be taken care would be because of search
        let htmlSubstring
        let substringStartIndex = index
        let previousType = parseResult[index]
        for (let i = index; i <= index + entry.length; i++) {
          const currentType = i === index + entry.length ? CharacterType.EOL : parseResult[i]

          if (previousType !== currentType) {
            const substring = currentText.substring(substringStartIndex, i)
            if (previousType === CharacterType.Search) {
              // A portion of the URL substring is included in the search results
              htmlSubstring = html`${htmlSubstring}<span
                  class="dt-fm color-fg-on-emphasis color-bg-attention-emphasis ${select}"
                  >${substring}</span
                >`
            } else {
              htmlSubstring = html`${htmlSubstring}${substring}`
            }
            previousType = currentType
            substringStartIndex = i
          }
        }
        return htmlSubstring
      }

      // Split the current text using the URL Regex pattern
      const splitUrls = currentText.split(URLRegex)
      let index = 0
      for (const entry of splitUrls) {
        if (entry === '') {
          continue
        }
        if (entry.match(URLRegex)) {
          const prefixMatch = entry.match(/^[([{]*/)
          const entryPrefix = prefixMatch?.[0] || ''
          let entrySuffix = ''
          if (entryPrefix) {
            const suffixChars = [...entryPrefix].map(matchingURLPunctuation).map(regexpEscape).reverse().join('')
            const escapedEndPunctuation = Object.values(URLPunctuation).map(regexpEscape).join('')
            const suffixRegex = new RegExp(`${suffixChars}[^${escapedEndPunctuation}}>]*(?=$)`)
            const suffixMatch = entry.match(suffixRegex)
            entrySuffix = suffixMatch?.[0] || ''
          }
          const entryUrl = entry.replace(entryPrefix, '').replace(entrySuffix, '')
          const prefixHtmlSubstring = handleHighlight(index, entryPrefix)
          const urlHtmlSubstring = handleHighlight(index + entryPrefix.length, entryUrl)
          const suffixHtmlSubstring = handleHighlight(index + entryPrefix.length + entryUrl.length, entrySuffix)
          output = html`${output}${prefixHtmlSubstring}<a target="_blank" rel="noopener noreferrer" href="${entryUrl}"
              >${urlHtmlSubstring}</a
            >${suffixHtmlSubstring}`
        } else {
          const htmlSubstring = handleHighlight(index, entry)
          output = html`${output}${htmlSubstring}`
        }
        index += entry.length
      }
      result = html`${result}<span class="${classNames.join(' ')}" style="${styles.join(';')}">${output}</span>`
    }

    return result
  }

  /**
   * Parses the content into lines with nodes.
   * @param content content to parse
   * @param resume optional cross-line state to resume incrementally from a committed line boundary
   */
  parseLines(
    content: string,
    resume?: IParseResume,
  ): {lines: ILine[]; completedActionLineIndices: number[]; checkpoint?: IParseCheckpoint} {
    const completedActionLineIndices: number[] = resume?.completedActionLineIndices ?? []
    // lines we return
    const lines: ILine[] = resume?.lines ?? []
    // accumulated nodes for a particular line
    let nodes: IParseNode[] = []

    // start of a particular line
    let lineStartIndex = resume?.startIndex ?? 0
    // Absolute offset of `content[0]` within the logical full log. When resuming an incremental
    // parse, `content` holds only the delta region (newly committed lines + pending tail); every
    // read of `content` subtracts `baseOffset` to map an absolute offset to a local one, and
    // `contentEnd` is the absolute offset one past the end of `content`.
    const baseOffset = resume?.baseOffset ?? 0
    const contentEnd = baseOffset + content.length
    // start of plain node content
    let plainNodeStart = unsetValue

    // tells to consider the default logic where we check for plain text etc.,
    let considerDefaultLogic = true

    // stores the command, to match one of the 'supportedCommands'
    let currentCommand = ''
    // helps in finding commands in our format "##[command]" or "[command]"
    let commandSeeker = ''

    // when line ends, this tells if there's any pending node
    let pendingLastNode: number = unsetValue

    const resetCommandVar = () => {
      commandSeeker = ''
      currentCommand = ''
    }

    const resetPlain = () => {
      plainNodeStart = unsetValue
    }

    const resetPending = () => {
      pendingLastNode = unsetValue
    }

    const parseCommandEnd = () => {
      // possible continuation of our well-known commands
      const commandIndex = supportedCommands.indexOf(currentCommand)
      // Single-bracket markers (no "##" prefix) only come from live logs, and only
      // structural commands are valid in that form. Reject annotation-style
      // single-bracket text like "[INFO]"/"[ERROR]" so it renders verbatim.
      // (github/c2c-actions-support#6249)
      const singleBracketAllowed = commandSeeker !== '[' || liveLogCommands.has(currentCommand)
      if (commandIndex !== -1 && singleBracketAllowed) {
        considerDefaultLogic = false
        // we reached the end and found the command
        resetPlain()
        // command is for the whole line, so we are not pushing the node here but defering this to when we find the new line
        /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
        pendingLastNode = commandToType[currentCommand]!

        if (
          currentCommand === SECTION ||
          currentCommand === GROUP ||
          currentCommand === END_GROUP ||
          currentCommand === COMMAND ||
          currentCommand === ERROR ||
          currentCommand === WARNING ||
          currentCommand === NOTICE ||
          currentCommand === START_ACTION ||
          currentCommand === END_ACTION
        ) {
          // strip off ##[$(currentCommand)] if there are no timestamps at start
          const possibleTimestamp =
            content.substring(lineStartIndex - baseOffset, lineStartIndex + TimestampLength - baseOffset) || ''
          if (!possibleTimestamp.match(TimestampRegex)) {
            // Replace command only if it's found at the beginning of the line
            if (possibleTimestamp.indexOf(currentCommand) < 4) {
              // ## is optional, so pick the right offset
              const offset =
                content.substring(lineStartIndex - baseOffset, lineStartIndex + 2 - baseOffset) === '##' ? 4 : 2
              lineStartIndex = lineStartIndex + offset + currentCommand.length
            }
          }
        }

        if (currentCommand === GROUP) {
          groupStarted = true
        }

        if (currentCommand === START_ACTION) {
          actionStarted = true
        }

        if (currentCommand === END_ACTION) {
          actionEnded = true
        }

        // group logic- happyCase1: we found endGroup and there's already a group starting
        if (currentCommand === END_GROUP && currentGroupNode) {
          groupEnded = true
        }
      }

      resetCommandVar()
    }

    let groupStarted = false
    let groupEnded = false
    let currentGroupNode: IParseNode | undefined = resume?.currentGroupNode
    let nodeIndex = resume?.nodeIndex ?? 0
    let hiddenLineCount = resume?.hiddenLineCount ?? 0

    // Action stack for tracking nested composite action regions
    let actionStarted = false
    let actionEnded = false
    const actionStack: IParseNode[] = resume?.actionStack ?? []

    // Incremental checkpoint capture: snapshot cross-line state when a line begins at the
    // requested offset (the start of the pending tail), so the next append can resume there.
    const checkpointOffset = resume?.checkpointOffset
    let checkpoint: IParseCheckpoint | undefined
    const captureCheckpoint = () => {
      checkpoint = {
        lineStartIndex,
        nodeIndex,
        hiddenLineCount,
        currentGroupNode,
        actionStack: actionStack.slice(),
        linesLength: lines.length,
        caiLength: completedActionLineIndices.length,
      }
    }
    if (checkpointOffset !== undefined && lineStartIndex === checkpointOffset) {
      captureCheckpoint()
    }

    for (let index = lineStartIndex; index < contentEnd; index++) {
      const char = content.charAt(index - baseOffset)
      // start with considering default logic, individual conditions are responsible to set it false
      considerDefaultLogic = true
      if (char === newLineChar || index === contentEnd - 1) {
        if (char === commandEnd) {
          parseCommandEnd()
        }

        const node = {
          type: pendingLastNode,
          start: lineStartIndex,
          end: index,
          lineIndex: lines.length,
          hiddenLineCount,
        } as IParseNode

        let pushNode = false
        // end of the line/text, push any final nodes
        if (pendingLastNode !== NodeType.Plain) {
          // there's pending special node to be pushed
          pushNode = true
          // a new group has just started
          if (groupStarted) {
            currentGroupNode = node
            groupStarted = false
          }

          // a group has ended
          if (groupEnded && currentGroupNode) {
            // links to specifc lines in the UI need to match exactly what was provided by the runner for things like annotations so nodes can't be discarded
            // lineIndexes are further adjusted based on the number of groups to ensure consistent and continuout numbering of lines in the UI
            pushNode = true
            node.group = {
              lineIndex: currentGroupNode.lineIndex - 1,
              nodeIndex: currentGroupNode.index,
            }
            node.hiddenLineCount = hiddenLineCount
            currentGroupNode.isGroup = true

            // since group has ended, clear all of our pointers
            groupEnded = false
            currentGroupNode = undefined
            hiddenLineCount++
          }

          // Handle action start
          if (actionStarted) {
            const lineContent = content.substring(node.start - baseOffset, node.end + 1 - baseOffset)
            const props = this.parseActionProperties(lineContent)
            const actionInfo: IActionInfo = {
              id: props['id'] || '',
              display: props['display'] || 'Action',
              lineIndex: node.lineIndex,
              nodeIndex,
              depth: actionStack.length,
            }
            node.action = actionInfo
            node.isActionStart = true
            node.actionDisplay = actionInfo.display
            node.actionId = actionInfo.id
            if (actionStack.length > 0) {
              node.parentActionLineIndices = actionStack.map(a => a.lineIndex)
            }
            actionStack.push(node)
            actionStarted = false
          }

          // Handle action end
          if (actionEnded) {
            const lineContent = content.substring(node.start - baseOffset, node.end + 1 - baseOffset)
            const props = this.parseActionProperties(lineContent)
            const endId = props['id'] || ''

            // Find matching start in stack (walk from top)
            let matchIndex = -1
            for (let si = actionStack.length - 1; si >= 0; si--) {
              /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
              if (actionStack[si]!.action?.id === endId) {
                matchIndex = si
                break
              }
            }

            if (matchIndex >= 0) {
              /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
              const startNode = actionStack[matchIndex]!
              /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
              const actionInfo = startNode.action!
              actionInfo.outcome = props['outcome']
              actionInfo.conclusion = props['conclusion']
              const durationMs = parseInt(props['duration_ms'] || '', 10)
              if (!isNaN(durationMs)) actionInfo.durationMs = durationMs
              actionInfo.endLineIndex = node.lineIndex
              startNode.actionEndLineIndex = node.lineIndex

              // Set action info on end node
              node.action = actionInfo
              node.actionId = endId

              // Remove from stack
              actionStack.splice(matchIndex, 1)
              completedActionLineIndices.push(startNode.lineIndex)
            }

            node.hiddenLineCount = hiddenLineCount
            hiddenLineCount++
            actionEnded = false
          }
        } else if (pendingLastNode === NodeType.Plain) {
          // there's pending plain node to be pushed
          pushNode = true
        }

        if (pushNode) {
          node.index = nodeIndex++
          nodes.push(node)
        }

        // A group is pending
        if (currentGroupNode && node !== currentGroupNode) {
          node.group = {
            lineIndex: currentGroupNode.lineIndex,
            nodeIndex: currentGroupNode.index,
          }
        }

        // Assign action info to nodes inside an action region
        if (actionStack.length > 0 && !node.isActionStart && node.type !== NodeType.EndAction) {
          /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
          const currentAction = actionStack[actionStack.length - 1]!
          node.action = currentAction.action
          if (actionStack.length > 1) {
            node.parentActionLineIndices = actionStack.slice(0, -1).map(a => a.lineIndex)
          }
        }

        // end of the line, push all nodes that are accumulated till now
        if (nodes.length > 0) {
          lines.push({nodes})
        }

        // clear node as we are done with the line
        nodes = []
        // increment lineStart for the next line
        lineStartIndex = index + 1
        // unset
        resetPlain()
        resetPending()
        resetCommandVar()
        considerDefaultLogic = false

        if (checkpointOffset !== undefined && lineStartIndex === checkpointOffset) {
          captureCheckpoint()
        }
      } else if (char === hashChar) {
        // possible start of our well-known commands
        if (commandSeeker === '' || commandSeeker === '#') {
          considerDefaultLogic = false
          commandSeeker += hashChar
        }
      } else if (char === commandStart) {
        // A workflow command in completed logs is prefixed with "##" (e.g.
        // ##[error]message). Live logs, however, emit structural markers WITHOUT
        // the "##" prefix (e.g. "[section]", "[command]"). We seek both forms here;
        // parseCommandEnd decides which single-bracket commands are valid so plain
        // user output like "[INFO - 12:00:00] ..." still renders verbatim.
        // (github/c2c-actions-support#6249)
        if (commandSeeker === '##') {
          considerDefaultLogic = false
          commandSeeker += commandStart
        } else if (commandSeeker.length === 0) {
          // covers "", for live logs commands are of the form "[section]" without "##"
          considerDefaultLogic = false
          commandSeeker += commandStart
        }
      } else if (char === ' ') {
        // Only composite action markers use a space to separate the command from its
        // key=value properties:
        //   ##[start-action display=Build;id=__run_1]
        //   ##[end-action id=__run_1;outcome=success;conclusion=success;duration_ms=1234]
        // Every other command is ]‑terminated (e.g. ##[error]message), so a space must
        // NOT finalize it as a command — otherwise plain user output such as
        // "##[INFO this is info" would be misparsed as an info/error/warning/debug
        // annotation. (github/c2c-actions-support#6249)
        if (
          commandSeeker === '##[' &&
          (currentCommand === START_ACTION || currentCommand === END_ACTION) &&
          supportedCommands.indexOf(currentCommand) !== -1
        ) {
          parseCommandEnd()
          lineStartIndex = index + 1
          considerDefaultLogic = false
        }
      } else if (char === commandEnd) {
        if (currentCommand === ICON) {
          const startIndex = index + 1
          let endIndex = startIndex
          for (let i = startIndex; i < contentEnd; i++) {
            const iconChar = content[i - baseOffset]
            if (iconChar === ' ') {
              endIndex = i
              break
            }
          }
          nodes.push({
            type: NodeType.Icon,
            lineIndex: lines.length,
            start: startIndex,
            end: endIndex,
            index: nodeIndex++,
            hiddenLineCount,
          })
          // jump to post Icon content
          index = endIndex + 1
          lineStartIndex = index
          continue
        } else {
          parseCommandEnd()
        }
      }

      if (considerDefaultLogic) {
        if (commandSeeker === '##[' || commandSeeker === '[') {
          // it's possible that we are parsing a command
          currentCommand += char.toLowerCase()
        }

        if (currentCommand.length > maxCommandLength) {
          // to avoid accumulating command unncessarily, let's check max length, if it exceeds, it's not a command
          resetCommandVar()
        }

        // considering as plain text
        if (plainNodeStart === unsetValue) {
          // we didn't set this yet, set now
          plainNodeStart = lineStartIndex
          // set pending node if there isn't one pending
          if (pendingLastNode === unsetValue) {
            pendingLastNode = NodeType.Plain
          }
        }
      }
    }

    return {lines, completedActionLineIndices, checkpoint}
  }

  /**
   * Parses semicolon-delimited key=value properties from action marker content.
   * Values are percent-encoded by the runner (e.g. spaces → %20, # → %23) and
   * decoded here. Falls back to the raw value if decoding fails.
   *
   * Example input: "display=My%20Action;id=__run_1;outcome=success;conclusion=success;duration_ms=1234]"
   * Example output: { display: "My Action", id: "__run_1", outcome: "success", conclusion: "success", duration_ms: "1234" }
   */
  parseActionProperties(lineContent: string): Record<string, string> {
    const props: Record<string, string> = {}
    const cleaned = lineContent.trimEnd().replace(/\]$/, '')
    for (const part of cleaned.split(';')) {
      const eqIndex = part.indexOf('=')
      if (eqIndex > 0) {
        const key = part.substring(0, eqIndex).trim()
        let value: string
        try {
          value = decodeURIComponent(part.substring(eqIndex + 1).trim())
        } catch {
          value = part.substring(eqIndex + 1).trim()
        }
        props[key] = value
      }
    }
    return props
  }

  /**
   * Parses the content into ANSII states
   * @param content content to parse
   */
  getStates(content: string): IAnsiEscapeCodeState[] {
    const result: IAnsiEscapeCodeState[] = []
    // Eg: "ESC[0KESC[33;1mWorker informationESC[0m
    if (!_ansiEscapeCodeRegex.test(content)) {
      // Not of interest, don't touch content
      return [
        {
          output: content,
        },
      ]
    }

    let command = ''
    let currentText = ''
    let code = ''
    let state = {} as IAnsiEscapeCodeState
    let isCommandActive = false
    let codes = []
    for (let index = 0; index < content.length; index++) {
      const character = content[index]
      if (isCommandActive) {
        if (character === ';') {
          if (code) {
            codes.push(code)
            code = ''
          }
        } else if (character === 'm') {
          if (code) {
            isCommandActive = false
            // done
            codes.push(code)
            state.style = state.style || ({} as IStyle)

            let setForeground = false
            let setBackground = false
            let isSingleColorCode = false
            let isRGBColorCode = false
            const rgbColors: number[] = []

            for (const currentCode of codes) {
              const style = state.style
              const codeNumber = parseInt(currentCode)
              if (setForeground && isSingleColorCode) {
                // set foreground color using 8-bit (256 color) palette - Esc[ 38:5:<n> m
                if (codeNumber >= 0 && codeNumber < 16) {
                  style.fg = this._get8BitColorClasses(codeNumber)
                } else if (codeNumber >= 16 && codeNumber < 256) {
                  style.fg = this._get8BitRGBColors(codeNumber)
                  style.isFgRGB = true
                }
                setForeground = false
                isSingleColorCode = false
              } else if (setForeground && isRGBColorCode) {
                // set foreground color using 24-bit (true color) palette - Esc[ 38:2:<r>:<g>:<b> m
                if (codeNumber >= 0 && codeNumber < 256) {
                  rgbColors.push(codeNumber)
                  if (rgbColors.length === 3) {
                    style.fg = `${rgbColors[0]},${rgbColors[1]},${rgbColors[2]}`
                    style.isFgRGB = true
                    rgbColors.length = 0 // clear array
                    setForeground = false
                    isRGBColorCode = false
                  }
                }
              } else if (setBackground && isSingleColorCode) {
                // set background color using 8-bit (256 color) palette - Esc[ 48:5:<n> m
                if (codeNumber >= 0 && codeNumber < 16) {
                  style.bg = this._get8BitColorClasses(codeNumber)
                } else if (codeNumber >= 16 && codeNumber < 256) {
                  style.bg = this._get8BitRGBColors(codeNumber)
                  style.isBgRGB = true
                }
                setBackground = false
                isSingleColorCode = false
              } else if (setBackground && isRGBColorCode) {
                // set background color using 24-bit (true color) palette - Esc[ 48:2:<r>:<g>:<b> m
                if (codeNumber >= 0 && codeNumber < 256) {
                  rgbColors.push(codeNumber)
                  if (rgbColors.length === 3) {
                    style.bg = `${rgbColors[0]},${rgbColors[1]},${rgbColors[2]}`
                    style.isBgRGB = true
                    rgbColors.length = 0 // clear array
                    setBackground = false
                    isRGBColorCode = false
                  }
                }
              } else if (setForeground || setBackground) {
                if (codeNumber === 5) {
                  isSingleColorCode = true
                } else if (codeNumber === 2) {
                  isRGBColorCode = true
                }
              } else if (fgColors[currentCode]) {
                /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
                style.fg = fgColors[currentCode]!
              } else if (bgColors[currentCode]) {
                /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
                style.bg = bgColors[currentCode]!
              } else if (currentCode === Resets.Reset) {
                // reset
                state.style = {} as IStyle
              } else if (currentCode === Resets.Set_Bg) {
                setBackground = true
              } else if (currentCode === Resets.Set_Fg) {
                setForeground = true
              } else if (currentCode === Resets.Default_Fg) {
                style.fg = ''
              } else if (currentCode === Resets.Default_Bg) {
                style.bg = ''
              } else if (codeNumber >= 91 && codeNumber <= 97) {
                style.fg = fgColors[codeNumber - 60] + BrightClassPostfix
              } else if (codeNumber >= 101 && codeNumber <= 107) {
                style.bg = bgColors[codeNumber - 60] + BrightClassPostfix
              } else if (specials[currentCode]) {
                style[specials[currentCode]] = true
              } else if (currentCode === Resets.Bold) {
                style.bold = false
              } else if (currentCode === Resets.Italic) {
                style.italic = false
              } else if (currentCode === Resets.Underline) {
                style.underline = false
              }
            }

            // clear
            command = ''
            currentText = ''
            code = ''
          } else {
            // To handle ESC[m, we should just ignore them
            isCommandActive = false
            command = ''
            state.style = {} as IStyle
          }

          codes = []
          /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
        } else if (isNaN(parseInt(character!))) {
          // if this is not a number, eg: 0K, this isn't something we support
          code = ''
          isCommandActive = false
          command = ''
        } else if (code.length === 4) {
          // we probably got code that we don't support, ignore
          code = ''
          isCommandActive = false
          if (character !== ESC) {
            // if this is not an ESC, let's not consider command from now on
            // eg: ESC[0Ksometexthere, at this point, code would be 0K, character would be 's'
            command = ''
            currentText += character
          }
        } else {
          code += character
        }

        continue
      } else if (command) {
        if (command === ESC && character === '[') {
          isCommandActive = true
          // push state
          if (currentText) {
            state.output = currentText
            result.push(state)
            // deep copy exisiting style for the line to preserve different styles between commands
            let previousStyle
            if (state.style) {
              previousStyle = Object.assign({}, state.style)
            }
            state = {} as IAnsiEscapeCodeState
            if (previousStyle) {
              state.style = previousStyle
            }
            currentText = ''
          }
        }

        continue
      }

      if (character === ESC) {
        command = character
      } else {
        currentText += character
      }
    }

    // still pending text
    if (currentText) {
      state.output = currentText + (command ? command : '')
      result.push(state)
    }

    return result
  }

  /**
   * With 8 bit colors, from 16-256, rgb color combinations are used
   * 16-231 (216 colors) is a 6 x 6 x 6 color cube
   * 232 - 256 are grayscale colors
   * @param codeNumber 16-256 number
   */
  private _get8BitRGBColors(codeNumber: number): string {
    let rgbColor: IRGBColor
    if (codeNumber < 232) {
      rgbColor = this._get216Color(codeNumber - 16)
    } else {
      rgbColor = this._get8bitGrayscale(codeNumber - 232)
    }
    return `${rgbColor.r},${rgbColor.g},${rgbColor.b}`
  }

  /**
   * With 8 bit color, from 0-15, css classes are used to represent customer colors
   * @param codeNumber 0-15 number that indicates the standard or high intensity color code that should be used
   */
  private _get8BitColorClasses(codeNumber: number): string {
    let colorClass = ''
    if (codeNumber < 8) {
      colorClass = `${base8BitColors[codeNumber]}`
    } else {
      colorClass = `${base8BitColors[codeNumber - 8] + BrightClassPostfix}`
    }
    return colorClass
  }

  /**
   * 6 x 6 x 6 (216 colors) rgb color generator
   * https://en.wikipedia.org/wiki/Web_colors#Web-safe_colors
   * @param increment 0-215 value
   */
  private _get216Color(increment: number): IRGBColor {
    return {
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      r: colorIncrements216[Math.floor(increment / 36)]!,
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      g: colorIncrements216[Math.floor(increment / 6) % 6]!,
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      b: colorIncrements216[increment % 6]!,
    }
  }

  /**
   * Grayscale from black to white in 24 steps. The first value of 0 represents rgb(8,8,8) while the last value represents rgb(238,238,238)
   * @param increment 0-23 value
   */
  private _get8bitGrayscale(increment: number): IRGBColor {
    const colorCode = increment * 10 + 8
    return {
      r: colorCode,
      g: colorCode,
      b: colorCode,
    }
  }
}
